import Promise from 'bluebird';
import { push } from 'react-router-redux';
import {
    getConfig,
    checkRegCode,
    getAuthenticateUrl,
    getAuthSuccessUrl,
    getAuthErrorUrl
} from 'lib/api/AdobeClientlessAPI';
import { removeQueryFromUrl } from 'lib/format';
import { ModalType, AppEnvs } from 'enums';
import { displayModalMessage } from 'common/actions/appActions';
import * as oneId from 'lib/oneId';
import { isSubscribeInEntitlementPath, generateCommerceFlowUrl } from './util';
import {
    getOneIdApiKey,
    getOneIdEmail,
    updateOneIdMarkettingOptIn
} from 'lib/api/oneIdApi';
import {
    WEB_ESPN_PLUS_PURCHASE_PATH_QA,
    WEB_ESPN_PLUS_PURCHASE_PATH_PROD
} from './constants';

export const GET_CONFIG = 'GET_CONFIG';
export const REG_CODE_UPDATE = 'REG_CODE_UPDATE';
export const AUTHENTICATE = 'AUTHENTICATE';
export const GENERIC_ERROR = 'GENERIC_ERROR';
export const INVALID_ERROR = 'INVALID_ERROR';
export const START_SUBMIT = 'START_SUBMIT';
export const COMPLETE_SUBMIT = 'COMPLETE_SUBMIT';
export const EPLUS_MARKETTING_OPT_IN_UPDATE = 'EPLUS_MARKETTING_OPT_IN_UPDATE';

export function createGetConfigAction(mvpd, message) {
    return {
        type: GET_CONFIG,
        mvpd,
        message
    };
}

export function createAuthenticateAction(authUrl, authSuccessUrl, authErrorUrl, useIframe) {
    return {
        type: AUTHENTICATE,
        authUrl,
        authSuccessUrl,
        authErrorUrl,
        useIframe
    };
}

export function createGenericErrorAction(message, providerId) {
    return {
        type: GENERIC_ERROR,
        message,
        providerId
    };
}

export function createInvalidErrorAction(message, providerId) {
    return {
        type: INVALID_ERROR,
        message,
        providerId
    };
}

// Actions
export function regCodeUpdate(code) {
    return {
        type: REG_CODE_UPDATE,
        code
    };
}

export function startSubmit() {
    return {
        type: START_SUBMIT
    };
}

export function completeSubmit() {
    return {
        type: COMPLETE_SUBMIT
    };
}

export function updateOneIdMarkettingOptInAction(payload) {
    return {
        type: EPLUS_MARKETTING_OPT_IN_UPDATE,
        payload
    };
}

export function getClientlessConfig() {
    return (dispatch) => (
        getConfig()
            .then(response => {
                const { requestor: { mvpds: { mvpd = [] } = {} } = {}, message } = response;
                return dispatch(createGetConfigAction(mvpd, message));
            })
    );
}

export function checkAdobeRegCode(regCode, env) {
    return async (dispatch) => {
        const { info: { deviceType } = {}, message } = await checkRegCode(regCode, env);
        if (message) {
            throw new Error(message);
        }
        dispatch(push({
            pathname: '/activate',
            query: {
                deviceType,
                regCode,
                chooseProvider: true
            }
        }));
    };
}

export function launchLicensePlateOnSuccess(oneIdClientUrl, swid, accessToken) {
    return async (dispatch) => {
        try {
            // get oneId api key
            const oneIdApiKey = await getOneIdApiKey(oneIdClientUrl);
            if (oneIdApiKey) {
                // get oneId email
                const { isSuccess, email } = await getOneIdEmail(oneIdClientUrl, swid, accessToken);
                if (isSuccess) {
                    const res = await updateOneIdMarkettingOptIn(oneIdClientUrl, oneIdApiKey, email);
                    dispatch(updateOneIdMarkettingOptInAction({
                        isSuccess: res.isSuccess,
                        err: res.err
                    }));
                }
            }
        } catch (err) {
            dispatch(updateOneIdMarkettingOptInAction({
                err,
                isSuccess: false
            }));
        }
    };
}

export function checkOneIdRegCode(deviceType, pairingCode, commerceUrlsConfig, qs) {
    return async (dispatch, getState) => {
        try {
            const {
                app: { env = '' }
            } = getState();
            const { lang, country, urls } = commerceUrlsConfig;
            const dataBag = (await oneId.getLicensePlateDataBag({ pairingCode })) || {};
            const {
                entitlementPath,
                correlationId,
                packages,
                entitlements,
                deviceType: dataBagDevice,
                commercePath
            } = dataBag;
            const deviceName = dataBagDevice || deviceType;

            await oneId.launchLicensePlate({
                deviceName,
                authenticationFlow: entitlementPath,
                correlationId,
                pairingCode
            });

            // when it reaches here, that means activation code (License Plate) was submitted successfully.
            const commerceBase = env !== AppEnvs.prod ? WEB_ESPN_PLUS_PURCHASE_PATH_QA : WEB_ESPN_PLUS_PURCHASE_PATH_PROD;
            let commerceUrl = commercePath
                ? `${commerceBase}/${commercePath}?start=login`
                : null;

            // if commerceUrl is empty, generate commerceUrl from urls mapping that is set in app-configurations
            if (!commerceUrl) {
                const packagesList = packages || entitlements;
                const generateCommerceUrl = generateCommerceFlowUrl(
                    urls,
                    packagesList,
                    deviceName,
                    lang,
                    country
                );

                const isSubscribePath = isSubscribeInEntitlementPath(entitlementPath);

                // If `subscribe` is in the entitlementPath property and
                // generateCommerceUrl is not empty, set commerceUrl.
                if (isSubscribePath && generateCommerceUrl !== '') {
                    commerceUrl = generateCommerceUrl;
                }
            }

            // from the external config file; redirect only if window.location is available
            if (
                commerceUrl &&
                window &&
                window.location &&
                window.location.href
            ) {
                window.location.href = commerceUrl;
            } else {
                dispatch(
                    push({
                        pathname: '/activate',
                        query: {
                            ...qs,
                            authSuccess: true,
                            deviceType: deviceName
                        }
                    })
                );
            }
        } catch (err) {
            throw err;
        }
    };
}

export function clientlessAuthenticate({
    regCode,
    mvpd,
    deviceType,
    provider,
    baseUrl: origBaseUrl = window.location.href,
    adobePassEnv
}) {
    return (dispatch) => {
        const baseUrl = removeQueryFromUrl(origBaseUrl, 'chooseProvider,deviceType,regCode');
        const authUrl = getAuthenticateUrl({ regCode, mvpd, deviceType, baseUrl, adobePassEnv });
        const authSuccessUrl = getAuthSuccessUrl({ mvpd, deviceType, baseUrl });
        const authErrorUrl = getAuthErrorUrl({ baseUrl });
        const useIframe = provider.iFrameRequired && provider.iFrameRequired.value && window.innerWidth > 500;

        dispatch(createAuthenticateAction(authUrl, authSuccessUrl, authErrorUrl, useIframe));

        try {
            if (useIframe) {
                // open url in an iframe (use modal)
                dispatch(displayModalMessage(ModalType.IFRAME_AUTH, {
                    src: authUrl,
                    width: (provider.iFrameWidth && provider.iFrameWidth.value) || 500,
                    height: (provider.iFrameHeight && provider.iFrameHeight.value) || 500
                }));
            } else {
                window.location.href = authUrl;
            }
        } catch (err) {
            return Promise.reject(err);
        }

        return Promise.resolve(authUrl);
    };
}



// WEBPACK FOOTER //
// ./app/webapp/page/Activate/actions.js